import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/features/auth/AuthContext";
import { api } from "@/features/auth/api";
import { formatApiError } from "@/features/auth/formatError";

/**
 * Admin profile — for any admin role (super/ops/store/staff). Shows core
 * account info and lets the user change their password. Editing the display
 * name & phone goes through the customer-side `PATCH /auth/me` endpoint
 * (works for any authenticated user, not just customers).
 */
export default function AdminProfile() {
    const { user, refresh } = useAuth();
    const [name, setName] = useState(user?.full_name ?? "");
    const [phone, setPhone] = useState(user?.phone ?? "");
    const [savingProfile, setSavingProfile] = useState(false);

    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [changing, setChanging] = useState(false);

    const saveProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setSavingProfile(true);
        try {
            await api.patch("/auth/me", { full_name: name.trim(), phone: phone.trim() });
            await refresh?.();
            toast.success("Profile updated");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSavingProfile(false);
        }
    };

    const changePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (next !== confirm) {
            toast.error("New password and confirmation do not match");
            return;
        }
        if (next.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        setChanging(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password changed");
            setCurrent("");
            setNext("");
            setConfirm("");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setChanging(false);
        }
    };

    if (!user) return null;

    return (
        <div data-testid="admin-profile-page" className="space-y-8 max-w-2xl">
            <header className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-ink text-white">
                    <ShieldCheck className="h-5 w-5" />
                </span>
                <div>
                    <h1 className="text-2xl font-extrabold">My profile</h1>
                    <p className="text-sm text-muted-foreground">
                        Signed in as <span className="font-bold">{user.email}</span> · role: {user.role.replace(/_/g, " ")}
                    </p>
                </div>
            </header>

            {/* Profile details */}
            <section className="rounded-2xl border border-border/60 bg-white p-6">
                <h2 className="text-lg font-bold">Account details</h2>
                <form className="mt-4 space-y-3" onSubmit={saveProfile}>
                    <div>
                        <Label htmlFor="p-name">Full name</Label>
                        <Input id="p-name" data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} required />
                    </div>
                    <div>
                        <Label htmlFor="p-phone">Phone</Label>
                        <Input id="p-phone" data-testid="profile-phone" value={phone} onChange={(e) => setPhone(e.target.value)} required />
                    </div>
                    <Button type="submit" className="bg-brand text-white" data-testid="profile-save" disabled={savingProfile}>
                        {savingProfile ? "Saving…" : "Save profile"}
                    </Button>
                </form>
            </section>

            {/* Password change */}
            <section className="rounded-2xl border border-border/60 bg-white p-6">
                <div className="flex items-center gap-2">
                    <KeyRound className="h-4 w-4 text-brand" />
                    <h2 className="text-lg font-bold">Change password</h2>
                </div>
                <form className="mt-4 space-y-3" onSubmit={changePassword}>
                    <div>
                        <Label htmlFor="p-cur">Current password</Label>
                        <Input id="p-cur" type="password" data-testid="profile-current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" />
                    </div>
                    <div>
                        <Label htmlFor="p-new">New password</Label>
                        <Input id="p-new" type="password" data-testid="profile-new-password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={6} autoComplete="new-password" />
                    </div>
                    <div>
                        <Label htmlFor="p-conf">Confirm new password</Label>
                        <Input id="p-conf" type="password" data-testid="profile-confirm-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6} autoComplete="new-password" />
                    </div>
                    <Button type="submit" className="bg-brand text-white" data-testid="profile-change-password" disabled={changing}>
                        {changing ? "Updating…" : "Change password"}
                    </Button>
                </form>
            </section>
        </div>
    );
}
